/**
 * userManagementApi — pure axios functions for the admin users/clients/programs
 * endpoints. Intentionally side-effect free so they can be used directly in
 * React Query hooks.
 */

import api from "./api_instance";
import { API_ENDPOINTS } from "../constants/api";
import type {
  AdminClient,
  AdminProgram,
  AdminUser,
  CreateAdminUserPayload,
  UpdateAdminUserProfilePayload,
} from "../types/userManagement";

const withParams = (
  endpoint: string,
  params: Record<string, string | number>,
): string =>
  Object.entries(params).reduce(
    (resolved, [key, value]) =>
      resolved.replace(`:${key}`, encodeURIComponent(String(value))),
    endpoint,
  );

/** GET /admin/users */
export const getAdminUsersApi = async (): Promise<AdminUser[]> => {
  const { data } = await api.get<AdminUser[]>(API_ENDPOINTS.ADMIN.USERS);
  return data;
};

/** POST /admin/users */
export const createAdminUserApi = async (
  payload: CreateAdminUserPayload,
): Promise<AdminUser> => {
  const { data } = await api.post<AdminUser>(
    API_ENDPOINTS.ADMIN.USERS,
    payload,
  );
  return data;
};

/** PATCH /admin/users/:user_id — profile fields only (first/last name, email) */
export const updateAdminUserProfileApi = async (
  userId: number,
  payload: UpdateAdminUserProfilePayload,
): Promise<AdminUser> => {
  const { data } = await api.patch<AdminUser>(
    withParams(API_ENDPOINTS.ADMIN.USER_DETAIL, { user_id: userId }),
    payload,
  );
  return data;
};

/** PATCH /admin/users/:user_id/role */
export const updateAdminUserRoleApi = async (
  userId: number,
  roleId: number,
): Promise<AdminUser> => {
  const { data } = await api.patch<AdminUser>(
    withParams(API_ENDPOINTS.ADMIN.USER_ROLE, { user_id: userId }),
    { role_id: roleId },
  );
  return data;
};

/** PATCH /admin/users/:user_id/password — set a new password */
export const updateAdminUserPasswordApi = async (
  userId: number,
  password: string,
): Promise<void> => {
  await api.patch(
    withParams(API_ENDPOINTS.ADMIN.USER_PASSWORD, { user_id: userId }),
    { password },
  );
};

/** PATCH /admin/users/:user_id/programs — full replace.
 *  The URL still says "programs"; the request/response field is product_ids. */
export const updateAdminUserProgramsApi = async (
  userId: number,
  productIds: number[],
): Promise<AdminUser> => {
  const { data } = await api.patch<AdminUser>(
    withParams(API_ENDPOINTS.ADMIN.USER_PROGRAMS, { user_id: userId }),
    { product_ids: productIds },
  );
  return data;
};

/** DELETE /admin/users/:user_id — soft delete (sets active=false) */
export const deleteAdminUserApi = async (userId: number): Promise<void> => {
  await api.delete(
    withParams(API_ENDPOINTS.ADMIN.USER_DETAIL, { user_id: userId }),
  );
};

/** PATCH /admin/users/:user_id/activate */
export const activateAdminUserApi = async (
  userId: number,
): Promise<AdminUser> => {
  const { data } = await api.patch<AdminUser>(
    withParams(API_ENDPOINTS.ADMIN.USER_ACTIVATE, { user_id: userId }),
  );
  return data;
};

/** GET /admin/clients */
export const getAdminClientsApi = async (): Promise<AdminClient[]> => {
  const { data } = await api.get<AdminClient[]>(API_ENDPOINTS.ADMIN.CLIENTS);
  return data;
};

/** GET /admin/programs — ?program_id= is super_admin-only, ignored otherwise */
export const getAdminProgramsApi = async (
  programId?: number,
): Promise<AdminProgram[]> => {
  const { data } = await api.get<AdminProgram[]>(API_ENDPOINTS.ADMIN.PROGRAMS, {
    params: programId ? { program_id: programId } : undefined,
  });
  return data;
};
